/**
 * Tome paper textures (lane N): a speckle grain and a fibre tile, drawn once
 * per page load into small PNG data URIs and published as the `--tome-grain`
 * and `--tome-fibre` custom properties on the document root.
 *
 * Why bitmaps: an SVG `feTurbulence` used as a CSS background is replayed as
 * vector paint, so the noise filter re-runs every time a tile of the page is
 * rasterised (and the tome's page re-rasterises whenever something on it
 * repaints). A PNG decodes once and tiles for free. Seeded, so every page
 * load draws the same paper. Without a 2D canvas (tests) the tokens stay
 * unset and the skins fall back to untextured paper.
 */
import { seededRandom } from './tomeShape';

let published = false;

function tile(size: number, draw: (ctx: CanvasRenderingContext2D, random: () => number) => void, seed: number): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  let ctx: CanvasRenderingContext2D | null = null;
  try { ctx = canvas.getContext('2d'); } catch { ctx = null; }
  if (!ctx) return null;
  draw(ctx, seededRandom(seed));
  try { return canvas.toDataURL('image/png'); } catch { return null; }
}

/** Sparse umber flecks of varied size and weight. */
function drawGrain(ctx: CanvasRenderingContext2D, random: () => number): void {
  for (let i = 0; i < 900; i++) {
    const x = random() * 180; const y = random() * 180;
    const dark = random() < 0.8;
    ctx.fillStyle = dark ? `rgba(84, 56, 23, ${0.05 + random() * 0.16})` : `rgba(255, 250, 235, ${0.1 + random() * 0.25})`;
    const r = random() < 0.92 ? 0.5 + random() * 0.6 : 0.9 + random() * 0.9;
    ctx.fillRect(x, y, r, r * (0.7 + random() * 0.6));
  }
}

/** Long, faint, mostly horizontal fibres; each is drawn wrapped so the tile repeats seamlessly. */
function drawFibre(ctx: CanvasRenderingContext2D, random: () => number): void {
  const size = 260;
  ctx.lineCap = 'round';
  for (let i = 0; i < 70; i++) {
    const x = random() * size; const y = random() * size;
    const length = 18 + random() * 70; const tilt = (random() - 0.5) * 0.25;
    const bend = (random() - 0.5) * 6;
    ctx.strokeStyle = random() < 0.75 ? `rgba(110, 78, 36, ${0.05 + random() * 0.07})` : `rgba(255, 248, 228, ${0.12 + random() * 0.14})`;
    ctx.lineWidth = 0.5 + random() * 0.8;
    for (const dx of [0, -size]) {
      for (const dy of [0, -size, size]) {
        ctx.beginPath();
        ctx.moveTo(x + dx, y + dy);
        ctx.quadraticCurveTo(x + dx + length / 2, y + dy + bend, x + dx + length, y + dy + length * tilt);
        ctx.stroke();
      }
    }
  }
}

/** Draw and publish the paper textures once. Safe to call on every tome mount. */
export function ensureTomeTextures(): void {
  if (published || typeof document === 'undefined') return;
  // jsdom has no 2D canvas and reports every attempt; tests render untextured paper.
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('jsdom')) return;
  published = true;
  const grain = tile(180, drawGrain, 0x7a11);
  const fibre = tile(260, drawFibre, 0xf1b7);
  const root = document.documentElement.style;
  if (grain) root.setProperty('--tome-grain', `url("${grain}")`);
  if (fibre) root.setProperty('--tome-fibre', `url("${fibre}")`);
}
